import type { AppDispatch, RootState } from '../index';
import {
  approvePending,
  listPending,
  rejectPending,
  submitPending,
  updatePending,
  type SubmitPendingInput,
  type UpdatePendingInput,
} from '../../lib/api/pending-api-client';
import {
  pendingActionFailed,
  pendingActionRequested,
  pendingActionSucceeded,
  pendingBodyUpdated,
  pendingByDateFailed,
  pendingByDateRequested,
  pendingByDateSucceeded,
  pendingListFailed,
  pendingListRequested,
  pendingListSucceeded,
  pendingSubmitFailed,
  pendingSubmitRequested,
  pendingSubmitSucceeded,
} from '../slices/pending-slice';

function requireIdToken(getState: () => RootState): string {
  const idToken = getState().auth.idToken;
  if (idToken === undefined || idToken === '') {
    throw new Error('not-authenticated');
  }
  return idToken;
}

export function fetchPendingList(page = 1) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const idToken = requireIdToken(getState);
    dispatch(pendingListRequested());
    try {
      const result = await listPending({ page }, idToken);
      dispatch(
        pendingListSucceeded({
          items: result.items,
          page: result.page,
          total: result.total,
          hasMore: result.hasMore,
        }),
      );
    } catch (error) {
      dispatch(pendingListFailed({ error: error instanceof Error ? error.message : String(error) }));
    }
  };
}

export function submitPendingThunk(input: SubmitPendingInput) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const idToken = requireIdToken(getState);
    dispatch(pendingSubmitRequested());
    try {
      await submitPending(input, idToken);
      dispatch(pendingSubmitSucceeded());
    } catch (error) {
      dispatch(pendingSubmitFailed({ error: error instanceof Error ? error.message : String(error) }));
    }
  };
}

export function approvePendingThunk(id: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const idToken = requireIdToken(getState);
    dispatch(pendingActionRequested({ kind: 'approve' }));
    try {
      await approvePending({ id }, idToken);
      dispatch(pendingActionSucceeded({ id, kind: 'approve' }));
    } catch (error) {
      dispatch(
        pendingActionFailed({
          error: error instanceof Error ? error.message : String(error),
          kind: 'approve',
        }),
      );
    }
  };
}

/**
 * Fetch every queue row (any status) whose `intendedPublishDate`
 * equals `date`. Populates `state.pending.byDate[date]` for the
 * admin daily-page overlay so admins see approved, pending,
 * rejected, and system-auto-approved items alongside each other on
 * any historical or future date. Landed 2026-09-12 so admins can
 * completely control future-day publish content.
 */
export function fetchPendingByDate(date: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const idToken = requireIdToken(getState);
    dispatch(pendingByDateRequested({ date }));
    try {
      const result = await listPending({ page: 1, intendedPublishDate: date }, idToken);
      dispatch(pendingByDateSucceeded({ date, items: result.items }));
    } catch (error) {
      dispatch(
        pendingByDateFailed({
          date,
          error: error instanceof Error ? error.message : String(error),
        }),
      );
    }
  };
}

/**
 * Edit-in-queue for a pending item (2026-09-13). Sends
 * `POST /UpdatePendingContentV1` with the new envelope body,
 * updates the byDate cache with the fresh version, and returns
 * `{ok, version}` so the calling drawer can advance its
 * expected-version state without triggering a full re-fetch.
 */
export function updatePendingThunk(input: UpdatePendingInput) {
  return async (
    dispatch: AppDispatch, getState: () => RootState,
  ): Promise<{ readonly ok: boolean; readonly version?: number; readonly error?: string }> => {
    const idToken = requireIdToken(getState);
    try {
      const result = await updatePending(input, idToken);
      dispatch(
        pendingBodyUpdated({
          id: input.id,
          body: input.body,
          version: result.version,
          lastActionAt: result.lastActionAt,
        }),
      );
      return { ok: true, version: result.version };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return { ok: false, error: message };
    }
  };
}

export function rejectPendingThunk(id: string, reason: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const idToken = requireIdToken(getState);
    dispatch(pendingActionRequested({ kind: 'reject' }));
    try {
      await rejectPending({ id, reason }, idToken);
      dispatch(pendingActionSucceeded({ id, kind: 'reject' }));
    } catch (error) {
      dispatch(
        pendingActionFailed({
          error: error instanceof Error ? error.message : String(error),
          kind: 'reject',
        }),
      );
    }
  };
}
